import { useEffect, useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Button,
  Paper,
  Stack,
  Grid,
} from '@mui/material';
import EmailIcon from '@mui/icons-material/Email';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import GroupIcon from '@mui/icons-material/Group';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import SecurityIcon from '@mui/icons-material/Security';
import SettingsIcon from '@mui/icons-material/Settings';
import IntegrationInstructionsIcon from '@mui/icons-material/IntegrationInstructions';
import AutoGraphIcon from '@mui/icons-material/AutoGraph';
import AnalyticsIcon from '@mui/icons-material/Analytics';
import SchoolIcon from '@mui/icons-material/School';
import MarkEmailReadIcon from '@mui/icons-material/MarkEmailRead';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import CountUp from 'react-countup';
import { useSpring, animated } from 'react-spring';
import ConsultationForm from './ConsultationForm';

const features = [
  {
    icon: <TrendingUpIcon sx={{ fontSize: 40, color: 'primary.main' }} />,
    title: 'Marketing Automation',
    description: 'End-to-end marketing automation solution with powerful campaign management and lead nurturing capabilities.'
  },
  {
    icon: <EmailIcon sx={{ fontSize: 40, color: 'primary.main' }} />,
    title: 'Email Marketing',
    description: 'Advanced email marketing features with dynamic content, A/B testing, and detailed analytics.'
  },
  {
    icon: <SecurityIcon sx={{ fontSize: 40, color: 'primary.main' }} />,
    title: 'Security & Compliance',
    description: 'Enterprise-grade security features and compliance with GDPR, CCPA, and other regulations.'
  },
  {
    icon: <GroupIcon sx={{ fontSize: 40, color: 'primary.main' }} />,
    title: 'Lead Management',
    description: 'Comprehensive lead scoring, segmentation, and lifecycle management tools.'
  },
  {
    icon: <ShoppingCartIcon sx={{ fontSize: 40, color: 'primary.main' }} />,
    title: 'CRM Integration',
    description: 'Seamless integration with popular CRM systems and marketing tools.'
  },
  {
    icon: <AnalyticsIcon sx={{ fontSize: 40, color: 'primary.main' }} />,
    title: 'Advanced Analytics',
    description: 'Detailed reporting and analytics to track campaign performance and ROI.'
  }
];

const serviceCategories = [
  {
    title: 'Mautic Setup & Configuration',
    icon: <SettingsIcon sx={{ fontSize: 32, color: 'primary.main' }} />,
    services: [
      'Mautic Installation & Setup',
      'Server Configuration & Optimization',
      'Email Configuration & SMTP Setup',
      'Domain & SSL Configuration'
    ]
  },
  {
    title: 'Marketing Automation',
    icon: <IntegrationInstructionsIcon sx={{ fontSize: 32, color: 'primary.main' }} />,
    services: [
      'Campaign Workflow Design',
      'Lead Scoring & Segmentation',
      'Email Template Development',
      'A/B Testing Setup'
    ]
  },
  {
    title: 'Performance & Security',
    icon: <AutoGraphIcon sx={{ fontSize: 32, color: 'primary.main' }} />,
    services: [
      'Database Optimization',
      'Queue Management',
      'Security Hardening',
      'GDPR Compliance Setup'
    ]
  },
  {
    title: 'Integration Services',
    icon: <AnalyticsIcon sx={{ fontSize: 32, color: 'primary.main' }} />,
    services: [
      'CRM Integration (HubSpot, Salesforce)',
      'API Development & Webhooks',
      'Form Integration',
      'Social Media Integration'
    ]
  },
  {
    title: 'Analytics & Reporting',
    icon: <MarkEmailReadIcon sx={{ fontSize: 32, color: 'primary.main' }} />,
    services: [
      'Campaign Analytics Setup',
      'Custom Report Creation',
      'ROI Tracking',
      'Performance Dashboards'
    ]
  },
  {
    title: 'Training & Support',
    icon: <SchoolIcon sx={{ fontSize: 32, color: 'primary.main' }} />,
    services: [
      'Team Training Sessions',
      'Documentation Creation',
      '24/7 Technical Support',
      'Regular Maintenance'
    ]
  }
];

const heroMetrics = [
  { label: 'Deployments', value: 25, suffix: '+', color: '#0B60B1' },
  { label: 'Success Rate', value: 97, suffix: '%', color: '#4CAF50' },
  { label: 'Client Satisfaction', value: 99, suffix: '%', color: '#FF9800' }
];

const animatedData = [
  { month: 'Jan', leads: 150, conversions: 45, revenue: 25000 },
  { month: 'Feb', leads: 180, conversions: 54, revenue: 28000 },
  { month: 'Mar', leads: 220, conversions: 66, revenue: 32000 },
  { month: 'Apr', leads: 250, conversions: 75, revenue: 38000 },
  { month: 'May', leads: 280, conversions: 84, revenue: 42000 },
  { month: 'Jun', leads: 320, conversions: 96, revenue: 48000 }
];

export default function MauticPage() {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    setIsVisible(true);
  }, []);

  const fadeIn = useSpring({
    opacity: isVisible ? 1 : 0,
    transform: isVisible ? 'translateY(0)' : 'translateY(20px)',
    config: { duration: 1000 }
  });

  return (
    <Box sx={{ bgcolor: '#f7fafd', minHeight: '100vh' }}>
      {/* Hero Section */}
      <Box sx={{ bgcolor: 'primary.main', color: '#fff', py: { xs: 6, md: 10 }, position: 'relative' }}>
        <Container maxWidth="lg">
          <animated.div style={fadeIn}>
            <Box sx={{ textAlign: 'center', mb: 6 }}>
              <Typography variant="h3" fontWeight={700} gutterBottom>
                Mautic Consulting
              </Typography>
              <Typography variant="h5" sx={{ mb: 4, fontWeight: 400 }}>
                Expert deployment, configuration, and optimization of your Mautic marketing automation platform
              </Typography>
            </Box>

            {/* Animated Metrics */}
            <Box sx={{ display: 'flex', justifyContent: 'center', gap: 4, mb: 6 }}>
              {heroMetrics.map((metric, index) => (
                <Paper
                  key={index}
                  elevation={0}
                  sx={{
                    p: 3,
                    bgcolor: 'rgba(255, 255, 255, 0.1)',
                    borderRadius: 2,
                    minWidth: 200,
                    textAlign: 'center'
                  }}
                >
                  <Typography variant="h4" fontWeight={700} sx={{ color: 'white' }}>
                    <CountUp end={metric.value} suffix={metric.suffix} duration={2.5} />
                  </Typography>
                  <Typography variant="subtitle1" sx={{ color: 'white' }}>
                    {metric.label}
                  </Typography>
                </Paper>
              ))}
            </Box>

            {/* Animated Chart */}
            <Box sx={{ height: 300, bgcolor: 'rgba(255, 255, 255, 0.1)', borderRadius: 2, p: 3 }}>
              <ResponsiveContainer>
                <LineChart data={animatedData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(255, 255, 255, 0.1)" />
                  <XAxis dataKey="month" stroke="#fff" />
                  <YAxis yAxisId="left" stroke="#fff" />
                  <YAxis yAxisId="right" orientation="right" stroke="#fff" />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: 'rgba(0, 0, 0, 0.8)',
                      border: 'none',
                      borderRadius: 4
                    }}
                  />
                  <Line
                    yAxisId="left"
                    type="monotone"
                    dataKey="leads"
                    stroke="#0B60B1"
                    strokeWidth={2}
                    dot={false}
                  />
                  <Line
                    yAxisId="left"
                    type="monotone"
                    dataKey="conversions"
                    stroke="#4CAF50"
                    strokeWidth={2}
                    dot={false}
                  />
                  <Line
                    yAxisId="right"
                    type="monotone"
                    dataKey="revenue"
                    stroke="#FF9800"
                    strokeWidth={2}
                    dot={false}
                  />
                </LineChart>
              </ResponsiveContainer>
            </Box>
          </animated.div>
        </Container>
      </Box>

      {/* Features with Image Section */}
      <Box sx={{ py: 12, bgcolor: '#fff', position: 'relative', overflow: 'hidden' }}>
        {/* Background decorative elements */}
        <Box sx={{
          position: 'absolute',
          top: 0,
          right: 0,
          width: '40%',
          height: '100%',
          background: 'linear-gradient(135deg, rgba(11,96,177,0.05) 0%, rgba(11,96,177,0) 100%)',
          clipPath: 'polygon(100% 0, 100% 100%, 0 100%, 100% 0)',
          zIndex: 0
        }} />
        <Box sx={{
          position: 'absolute',
          bottom: 0,
          left: 0,
          width: '30%',
          height: '60%',
          background: 'linear-gradient(45deg, rgba(11,96,177,0.03) 0%, rgba(11,96,177,0) 100%)',
          clipPath: 'polygon(0 0, 100% 0, 0 100%, 0 0)',
          zIndex: 0
        }} />

        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 4, alignItems: 'stretch' }}>
            {/* Left Column - First 3 Cards */}
            <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 4 }}>
              <Stack spacing={4}>
                {features.slice(0, 3).map((feature, index) => (
                  <Paper
                    key={index}
                    elevation={0}
                    sx={{
                      p: 3,
                      bgcolor: 'rgba(255, 255, 255, 0.8)',
                      backdropFilter: 'blur(10px)',
                      borderRadius: 3,
                      transition: 'all 0.3s ease-in-out',
                      border: '1px solid rgba(11,96,177,0.1)',
                      position: 'relative',
                      overflow: 'hidden',
                      '&::before': {
                        content: '""',
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '4px',
                        height: '100%',
                        background: 'linear-gradient(to bottom, #0B60B1, #2196F3)',
                        opacity: 0,
                        transition: 'opacity 0.3s ease-in-out'
                      },
                      '&:hover': {
                        transform: 'translateX(8px)',
                        boxShadow: '0 8px 32px rgba(11,96,177,0.1)',
                        borderColor: 'rgba(11,96,177,0.2)',
                        '&::before': {
                          opacity: 1
                        }
                      }
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <Box sx={{ 
                        p: 1.5, 
                        borderRadius: 2, 
                        background: 'linear-gradient(45deg, #0B60B1 30%, #2196F3 90%)',
                        color: 'white',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        boxShadow: '0 4px 12px rgba(11,96,177,0.2)'
                      }}>
                        {feature.icon}
                      </Box>
                      <Typography variant="h6" sx={{ ml: 2, fontWeight: 600 }}>
                        {feature.title}
                      </Typography>
                    </Box>
                    <Typography variant="body1" color="text.secondary">
                      {feature.description}
                    </Typography>
                  </Paper>
                ))}
              </Stack>
            </Box>

            {/* Middle Column - Image */}
            <Box sx={{ 
              flex: 1.5,
              display: 'flex', 
              alignItems: 'center',
              position: 'relative',
              '&::before': {
                content: '""',
                position: 'absolute',
                top: '-20px',
                right: '-20px',
                width: '200px',
                height: '200px',
                background: 'linear-gradient(45deg, #0B60B1 0%, #2196F3 100%)',
                borderRadius: '50%',
                opacity: 0.1,
                zIndex: 0
              }
            }}>
              <Paper
                elevation={0}
                sx={{
                  width: '100%',
                  height: '600px',
                  borderRadius: 4,
                  overflow: 'hidden',
                  position: 'relative',
                  bgcolor: '#f7fafd',
                  boxShadow: '0 20px 40px rgba(11,96,177,0.1)',
                  transform: 'perspective(1000px) rotateY(-15deg) rotateX(5deg)',
                  transition: 'all 0.5s ease-in-out',
                  '&:hover': {
                    transform: 'perspective(1000px) rotateY(-5deg) rotateX(2deg)',
                    boxShadow: '0 30px 60px rgba(11,96,177,0.2)',
                  }
                }}
              >
                <Box
                  component="img"
                  src="/images/mautic.png"
                  alt="Mautic Platform"
                  sx={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                    objectPosition: 'center top',
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    transition: 'all 0.5s ease-in-out',
                    filter: 'brightness(0.95) contrast(1.1)',
                    '&:hover': {
                      filter: 'brightness(1) contrast(1.2)',
                    }
                  }}
                />
                <Box
                  sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    background: 'linear-gradient(180deg, rgba(11,96,177,0.1) 0%, rgba(11,96,177,0) 50%, rgba(0,0,0,0.7) 100%)',
                    zIndex: 1
                  }}
                />
                <Box
                  sx={{
                    position: 'absolute',
                    bottom: 0,
                    left: 0,
                    right: 0,
                    p: 6,
                    zIndex: 2
                  }}
                >
                  <Box sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    mb: 3,
                    '&::before': {
                      content: '""',
                      width: '40px',
                      height: '4px',
                      background: 'linear-gradient(90deg, #0B60B1, #2196F3)',
                      borderRadius: '2px',
                      mr: 2
                    }
                  }}>
                    <Typography variant="h5" sx={{ 
                      fontWeight: 700, 
                      color: 'white',
                      textShadow: '0 2px 4px rgba(0,0,0,0.2)'
                    }}>
                      Mautic Platform
                    </Typography>
                  </Box>
                  <Typography variant="subtitle1" sx={{ 
                    color: 'white', 
                    opacity: 0.9, 
                    mb: 4,
                    textShadow: '0 1px 2px rgba(0,0,0,0.2)'
                  }}>
                    Enterprise-grade marketing automation with advanced features
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 2 }}>
                    <Button 
                      variant="contained" 
                      sx={{ 
                        background: 'linear-gradient(45deg, #0B60B1 30%, #2196F3 90%)',
                        color: 'white',
                        px: 4,
                        py: 1.5,
                        borderRadius: 2,
                        boxShadow: '0 4px 12px rgba(11,96,177,0.3)',
                        '&:hover': {
                          background: 'linear-gradient(45deg, #0B60B1 40%, #2196F3 100%)',
                          boxShadow: '0 6px 16px rgba(11,96,177,0.4)',
                          transform: 'translateY(-2px)',
                        },
                        transition: 'all 0.3s ease-in-out'
                      }}
                    >
                      Learn More
                    </Button>
                    <Button 
                      variant="outlined" 
                      sx={{ 
                        borderColor: 'white',
                        color: 'white',
                        px: 4,
                        py: 1.5,
                        borderRadius: 2,
                        '&:hover': {
                          borderColor: 'white',
                          backgroundColor: 'rgba(255,255,255,0.1)',
                          transform: 'translateY(-2px)',
                        },
                        transition: 'all 0.3s ease-in-out'
                      }}
                    >
                      View Demo
                    </Button>
                  </Box>
                </Box>
              </Paper>
            </Box>

            {/* Right Column - Last 3 Cards */}
            <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 4 }}>
              <Stack spacing={4}>
                {features.slice(3, 6).map((feature, index) => (
                  <Paper
                    key={index}
                    elevation={0}
                    sx={{
                      p: 3,
                      bgcolor: 'rgba(255, 255, 255, 0.8)',
                      backdropFilter: 'blur(10px)',
                      borderRadius: 3,
                      transition: 'all 0.3s ease-in-out',
                      border: '1px solid rgba(11,96,177,0.1)',
                      position: 'relative',
                      overflow: 'hidden',
                      '&::before': {
                        content: '""',
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '4px',
                        height: '100%',
                        background: 'linear-gradient(to bottom, #0B60B1, #2196F3)',
                        opacity: 0,
                        transition: 'opacity 0.3s ease-in-out'
                      },
                      '&:hover': {
                        transform: 'translateX(8px)',
                        boxShadow: '0 8px 32px rgba(11,96,177,0.1)',
                        borderColor: 'rgba(11,96,177,0.2)',
                        '&::before': {
                          opacity: 1
                        }
                      }
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <Box sx={{ 
                        p: 1.5, 
                        borderRadius: 2, 
                        background: 'linear-gradient(45deg, #0B60B1 30%, #2196F3 90%)',
                        color: 'white',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        boxShadow: '0 4px 12px rgba(11,96,177,0.2)'
                      }}>
                        {feature.icon}
                      </Box>
                      <Typography variant="h6" sx={{ ml: 2, fontWeight: 600 }}>
                        {feature.title}
                      </Typography>
                    </Box>
                    <Typography variant="body1" color="text.secondary">
                      {feature.description}
                    </Typography>
                  </Paper>
                ))}
              </Stack>
            </Box>
          </Box>
        </Container>
      </Box>

      {/* Service Categories */}
      <Box sx={{ bgcolor: '#f7fafd', py: 12, position: 'relative', overflow: 'hidden' }}>
        {/* Background decorative elements */}
        <Box sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '30%',
          height: '100%',
          background: 'linear-gradient(135deg, rgba(11,96,177,0.05) 0%, rgba(11,96,177,0) 100%)',
          clipPath: 'polygon(0 0, 100% 0, 0 100%, 0 0)',
          zIndex: 0
        }} />
        <Box sx={{
          position: 'absolute',
          bottom: 0,
          right: 0,
          width: '40%',
          height: '60%',
          background: 'linear-gradient(45deg, rgba(11,96,177,0.03) 0%, rgba(11,96,177,0) 100%)',
          clipPath: 'polygon(100% 0, 100% 100%, 0 100%, 100% 0)',
          zIndex: 0
        }} />

        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
          <Box sx={{ textAlign: 'center', mb: 8 }}>
            <Typography 
              variant="h4" 
              component="h2" 
              sx={{ 
                fontWeight: 700,
                background: 'linear-gradient(45deg, #0B60B1 30%, #2196F3 90%)',
                backgroundClip: 'text',
                textFillColor: 'transparent',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                mb: 2
              }}
            >
              Our Services
            </Typography>
            <Typography variant="subtitle1" color="text.secondary" sx={{ maxWidth: '600px', mx: 'auto' }}>
              Comprehensive Mautic solutions tailored to your marketing automation needs
            </Typography>
          </Box>

          <Grid container spacing={4}>
            {serviceCategories.map((category, index) => (
              <Grid 
                key={index}
                component="div"
                sx={{
                  flexBasis: { xs: '100%', md: 'calc(33.333% - 32px)' },
                  maxWidth: { xs: '100%', md: 'calc(33.333% - 32px)' },
                  flexGrow: 0
                }}
              >
                <Paper
                  elevation={0}
                  sx={{
                    p: 4,
                    height: '100%',
                    bgcolor: 'rgba(255, 255, 255, 0.8)',
                    backdropFilter: 'blur(10px)',
                    borderRadius: 3,
                    transition: 'all 0.3s ease-in-out',
                    border: '1px solid rgba(11,96,177,0.1)',
                    position: 'relative',
                    overflow: 'hidden',
                    '&::before': {
                      content: '""',
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      width: '4px',
                      height: '100%',
                      background: 'linear-gradient(to bottom, #0B60B1, #2196F3)',
                      opacity: 0,
                      transition: 'opacity 0.3s ease-in-out'
                    },
                    '&:hover': {
                      transform: 'translateY(-8px)',
                      boxShadow: '0 12px 32px rgba(11,96,177,0.1)',
                      borderColor: 'rgba(11,96,177,0.2)',
                      '&::before': {
                        opacity: 1
                      }
                    }
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                    <Box sx={{ 
                      p: 1.5, 
                      borderRadius: 2, 
                      background: 'linear-gradient(45deg, #0B60B1 30%, #2196F3 90%)',
                      color: 'white',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      boxShadow: '0 4px 12px rgba(11,96,177,0.2)'
                    }}>
                      {category.icon}
                    </Box>
                    <Typography variant="h6" sx={{ ml: 2, fontWeight: 600 }}>
                      {category.title}
                    </Typography>
                  </Box>
                  <Stack spacing={2}>
                    {category.services.map((service, serviceIndex) => (
                      <Box 
                        key={serviceIndex} 
                        sx={{ 
                          display: 'flex', 
                          alignItems: 'center',
                          transition: 'all 0.3s ease-in-out',
                          '&:hover': {
                            transform: 'translateX(8px)'
                          }
                        }}
                      >
                        <CheckCircleIcon sx={{ 
                          color: 'primary.main', 
                          mr: 1.5, 
                          fontSize: 20,
                          transition: 'all 0.3s ease-in-out',
                          '&:hover': {
                            transform: 'scale(1.2)'
                          }
                        }} />
                        <Typography variant="body1" sx={{ color: 'text.secondary' }}>
                          {service}
                        </Typography>
                      </Box>
                    ))}
                  </Stack>
                </Paper>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* FAQ Section */}
      <Box sx={{ py: 12, bgcolor: '#fff', position: 'relative' }}>
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', mb: 8 }}>
            <Typography 
              variant="h4" 
              component="h2" 
              sx={{ 
                fontWeight: 700,
                background: 'linear-gradient(45deg, #0B60B1 30%, #2196F3 90%)',
                backgroundClip: 'text',
                textFillColor: 'transparent',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                mb: 2
              }}
            >
              Frequently Asked Questions
            </Typography>
            <Typography variant="subtitle1" color="text.secondary" sx={{ maxWidth: '600px', mx: 'auto' }}>
              Common questions about our Mautic consulting services
            </Typography>
          </Box>

          <Grid container spacing={4}>
            <Grid item xs={12} md={6}>
              <Paper
                elevation={0}
                sx={{
                  p: 4,
                  height: '100%',
                  bgcolor: 'rgba(255, 255, 255, 0.8)',
                  backdropFilter: 'blur(10px)',
                  borderRadius: 3,
                  border: '1px solid rgba(11,96,177,0.1)',
                  transition: 'all 0.3s ease-in-out',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: '0 12px 32px rgba(11,96,177,0.1)',
                    borderColor: 'rgba(11,96,177,0.2)',
                  }
                }}
              >
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, color: 'primary.main' }}>
                  What is Mautic and why should I use it?
                </Typography>
                <Typography variant="body1" color="text.secondary">
                  Mautic is an open-source marketing automation platform that helps you manage your marketing campaigns, track leads, and automate your marketing processes. It's ideal for businesses looking for a cost-effective, customizable solution with full control over their data and infrastructure.
                </Typography>
              </Paper>
            </Grid>

            <Grid item xs={12} md={6}>
              <Paper
                elevation={0}
                sx={{
                  p: 4,
                  height: '100%',
                  bgcolor: 'rgba(255, 255, 255, 0.8)',
                  backdropFilter: 'blur(10px)',
                  borderRadius: 3,
                  border: '1px solid rgba(11,96,177,0.1)',
                  transition: 'all 0.3s ease-in-out',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: '0 12px 32px rgba(11,96,177,0.1)',
                    borderColor: 'rgba(11,96,177,0.2)',
                  }
                }}
              >
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, color: 'primary.main' }}>
                  How long does it take to set up Mautic?
                </Typography>
                <Typography variant="body1" color="text.secondary">
                  A basic Mautic setup typically takes 2-3 days, including installation, configuration, and initial testing. For more complex deployments with custom integrations and workflows, the process may take 1-2 weeks. We'll provide a detailed timeline based on your specific requirements.
                </Typography>
              </Paper>
            </Grid>

            <Grid item xs={12} md={6}>
              <Paper
                elevation={0}
                sx={{
                  p: 4,
                  height: '100%',
                  bgcolor: 'rgba(255, 255, 255, 0.8)',
                  backdropFilter: 'blur(10px)',
                  borderRadius: 3,
                  border: '1px solid rgba(11,96,177,0.1)',
                  transition: 'all 0.3s ease-in-out',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: '0 12px 32px rgba(11,96,177,0.1)',
                    borderColor: 'rgba(11,96,177,0.2)',
                  }
                }}
              >
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, color: 'primary.main' }}>
                  What kind of support do you provide?
                </Typography>
                <Typography variant="body1" color="text.secondary">
                  We offer comprehensive support including 24/7 monitoring, regular maintenance, technical support, and team training. Our support packages can be customized to your needs, from basic maintenance to full-service management of your Mautic instance.
                </Typography>
              </Paper>
            </Grid>

            <Grid item xs={12} md={6}>
              <Paper
                elevation={0}
                sx={{
                  p: 4,
                  height: '100%',
                  bgcolor: 'rgba(255, 255, 255, 0.8)',
                  backdropFilter: 'blur(10px)',
                  borderRadius: 3,
                  border: '1px solid rgba(11,96,177,0.1)',
                  transition: 'all 0.3s ease-in-out',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: '0 12px 32px rgba(11,96,177,0.1)',
                    borderColor: 'rgba(11,96,177,0.2)',
                  }
                }}
              >
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, color: 'primary.main' }}>
                  Can Mautic integrate with my existing systems?
                </Typography>
                <Typography variant="body1" color="text.secondary">
                  Yes, Mautic offers extensive integration capabilities. We can connect it with popular CRM systems, email platforms, social media networks, and custom applications through APIs and webhooks. We'll help you set up and optimize these integrations for seamless data flow.
                </Typography>
              </Paper>
            </Grid>

            <Grid item xs={12} md={6}>
              <Paper
                elevation={0}
                sx={{
                  p: 4,
                  height: '100%',
                  bgcolor: 'rgba(255, 255, 255, 0.8)',
                  backdropFilter: 'blur(10px)',
                  borderRadius: 3,
                  border: '1px solid rgba(11,96,177,0.1)',
                  transition: 'all 0.3s ease-in-out',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: '0 12px 32px rgba(11,96,177,0.1)',
                    borderColor: 'rgba(11,96,177,0.2)',
                  }
                }}
              >
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, color: 'primary.main' }}>
                  How do you handle security and compliance?
                </Typography>
                <Typography variant="body1" color="text.secondary">
                  We implement enterprise-grade security measures including SSL encryption, GDPR compliance, data protection protocols, and regular security audits. Our team ensures your Mautic instance meets all relevant compliance requirements and industry standards.
                </Typography>
              </Paper>
            </Grid>

            <Grid item xs={12} md={6}>
              <Paper
                elevation={0}
                sx={{
                  p: 4,
                  height: '100%',
                  bgcolor: 'rgba(255, 255, 255, 0.8)',
                  backdropFilter: 'blur(10px)',
                  borderRadius: 3,
                  border: '1px solid rgba(11,96,177,0.1)',
                  transition: 'all 0.3s ease-in-out',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: '0 12px 32px rgba(11,96,177,0.1)',
                    borderColor: 'rgba(11,96,177,0.2)',
                  }
                }}
              >
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, color: 'primary.main' }}>
                  What kind of training do you provide?
                </Typography>
                <Typography variant="body1" color="text.secondary">
                  We offer comprehensive training programs covering Mautic basics, campaign management, automation workflows, and advanced features. Training can be conducted on-site or remotely, and we provide detailed documentation and ongoing support to ensure your team can effectively use the platform.
                </Typography>
              </Paper>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Consultation Form Section */}
      <Container maxWidth="lg">
        <ConsultationForm
          title="Ready to Transform Your Marketing Automation?"
          formName="mautic_consultation"
          formSource="mautic_page"
          bulletPoints={[
            'Set up and optimize your Mautic instance',
            'Configure marketing automation workflows',
            'Implement lead scoring and segmentation',
            'Set up campaign tracking and analytics',
            'Integrate with your CRM and tools',
            'Train your team on best practices'
          ]}
          additionalFields={[
            {
              name: 'business_size',
              label: 'Business Size',
              type: 'select',
              required: true,
              options: [
                { value: 'startup', label: 'Startup (1-10 employees)' },
                { value: 'small', label: 'Small Business (11-50 employees)' },
                { value: 'medium', label: 'Medium Business (51-200 employees)' },
                { value: 'large', label: 'Large Business (201-1000 employees)' },
                { value: 'enterprise', label: 'Enterprise (1000+ employees)' }
              ]
            },
            {
              name: 'current_platform',
              label: 'Current Marketing Platform',
              type: 'select',
              required: true,
              options: [
                { value: 'none', label: 'No current platform' },
                { value: 'hubspot', label: 'HubSpot' },
                { value: 'marketo', label: 'Marketo' },
                { value: 'pardot', label: 'Pardot' },
                { value: 'mailchimp', label: 'Mailchimp' },
                { value: 'other', label: 'Other' }
              ]
            },
            {
              name: 'primary_goal',
              label: 'Primary Goal',
              type: 'select',
              required: true,
              options: [
                { value: 'lead_generation', label: 'Lead Generation' },
                { value: 'lead_nurturing', label: 'Lead Nurturing' },
                { value: 'marketing_automation', label: 'Marketing Automation' },
                { value: 'crm_integration', label: 'CRM Integration' },
                { value: 'analytics', label: 'Analytics & Reporting' }
              ]
            }
          ]}
        />
      </Container>
    </Box>
  );
} 